/* eslint-disable no-undef */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  CSSProperties,
} from 'react';
import { Button, CircularProgress, Modal } from '@mui/material';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import PaymentsAPI from 'api/PaymentsAPI';
import CreditsAPI from 'api/CreditsAPI';
import { GroupAPI } from 'api/GroupAPI';
import { useSnackbar } from 'notistack';
import {
  databaseCreditPackPriceIds,
  databaseCreditPacks,
  databaseCombinedCreditPackPriceIds,
  productDatabaseCreditPackPriceIds,
  productDatabaseCreditPacks,
  useCreditPackCatalog,
} from 'pages/credit-plans/CreditCalculator';
import { resolveLicenseTypeForItem } from 'utils/licenseInventory';
import { legacyDatabasePackKind } from 'utils/legacyDatabasePack';
import { subscriptionSaveOutcome } from './subscriptionSaveOutcome';

const BUSINESS_VERIFICATION_PRICE_PER_CREDIT = 0.5;
const CORPORATE_OWNERSHIP_PRICE_PER_CREDIT = 0.75;
const BUSINESS_VERIFICATION_CREDIT_TIERS = [
  100, 200, 400, 800, 1000, 2000, 3000, 4000, 5000, 7500, 10000, 20000, 50000,
];

interface IEditAccess {
  curTeam: any;
  customerInfo: any;
  adminUser: any;
  /** Re-reads the team's Stripe customer, so `customerInfo` is current. */
  onSubscriptionsChanged?: () => Promise<unknown> | void;
}

interface ProductConfig {
  id: string;
  moduleName: string;
  label: string;
  price?: number;
  priceId: string;
  type: 'fixed' | 'credit';
}

interface EnterpriseConfig extends ProductConfig {
  fixedQuantity: number;
  fixedCredits: number;
}

interface SelectedSubscriptionItem {
  product: ProductConfig;
  quantity: number;
  freeTrialEnabled?: boolean;
  freeTrialDays?: number;
}

const PurchaseBadge: React.FC<{ kind: 'monthly' | 'one-time' }> = ({
  kind,
}) => {
  const isMonthly = kind === 'monthly';
  const rgb = isMonthly ? '72, 149, 239' : '245, 158, 11';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: '11px',
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: '999px',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        background: `rgba(${rgb}, 0.15)`,
        color: isMonthly ? 'var(--action-color)' : 'var(--mid-color)',
        border: `1px solid rgba(${rgb}, 0.35)`,
      }}
    >
      {isMonthly ? 'Monthly' : 'One-time purchase'}
    </span>
  );
};

const DEFAULT_FREE_TRIAL_DAYS = 3;
// Mirrors Stripe's 730-day (2-year) cap on trial_period_days.
const MAX_FREE_TRIAL_DAYS = 730;

interface ConfirmActionModalProps {
  title: string;
  description: React.ReactNode;
  isProcessing: boolean;
  processingLabel: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
}

// Matches the "Change Status" / "Confirm status change" modal chrome used in
// components/BiteDB/Modal (icon badge, title + subtitle, close X, footer).
const ConfirmActionModal: React.FC<ConfirmActionModalProps> = ({
  title,
  description,
  isProcessing,
  processingLabel,
  confirmLabel,
  onCancel,
  onConfirm,
}) => (
  <div className='audit-add-modal-cont'>
    <div className='audit-add-modal-cont-head'>
      <div className='audit-add-modal-head-cont-change'>
        <div className='audit-add-modal-head-cont-icon'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='24'
            height='24'
            viewBox='0 0 24 24'
            fill='none'
          >
            <path
              d='M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z'
              stroke='#4895EF'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
            <path
              d='M9 12L11 14L15 10'
              stroke='#4895EF'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </div>
        <div className='audit-add-modal-head-cont-supporting'>
          <h1>{title}</h1>
          <p>{description}</p>
        </div>
      </div>
      <div
        className='audit-add-modal-head-close'
        aria-disabled={isProcessing}
        onClick={() => {
          if (!isProcessing) {
            onCancel();
          }
        }}
      >
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='24'
          height='24'
          viewBox='0 0 24 24'
          fill='none'
        >
          <path
            d='M18 6L6 18M6 6L18 18'
            stroke='#85888E'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </div>
    </div>
    <div className='audit-modal-cont-footer'>
      <div className='audit-modal-cont-footer-content'>
        <div
          className='audit-modal-cont-footer-content-btn-cancel'
          aria-disabled={isProcessing}
          onClick={() => {
            if (!isProcessing) {
              onCancel();
            }
          }}
        >
          <div className='audit-modal-cont-footer-content-btn-text'>Cancel</div>
        </div>
        <div
          className='audit-modal-cont-footer-content-btn-upload'
          onClick={() => {
            if (!isProcessing) {
              onConfirm();
            }
          }}
        >
          <div
            className='audit-modal-cont-footer-content-btn-text'
            style={{ color: '#FFF' }}
          >
            {isProcessing ? (
              <>
                <CircularProgress
                  style={{ width: '30px', height: '30px', marginRight: '10px' }}
                  color='primary'
                />{' '}
                {processingLabel}
              </>
            ) : (
              confirmLabel
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const EditAccess: React.FC<IEditAccess> = ({
  curTeam,
  customerInfo,
  adminUser,
  onSubscriptionsChanged,
}) => {
  const [quantities, setQuantities] = useState<Record<string, number>>({
    standard: 0,
    pro: 0,
    business: 0,
  });
  const [basePlanTrialToggles, setBasePlanTrialToggles] = useState<
    Record<string, boolean>
  >({
    standard: false,
    pro: false,
    business: false,
  });
  const [basePlanTrialDays, setBasePlanTrialDays] = useState<
    Record<string, string>
  >({
    standard: String(DEFAULT_FREE_TRIAL_DAYS),
    pro: String(DEFAULT_FREE_TRIAL_DAYS),
    business: String(DEFAULT_FREE_TRIAL_DAYS),
  });

  // Bugfix: default to empty string so 'None' is the unselected state
  const [selectedDatabaseCreditPack, setSelectedDatabaseCreditPack] =
    useState('');
  const [
    selectedProductDatabaseCreditPack,
    setSelectedProductDatabaseCreditPack,
  ] = useState('');

  const [
    selectedCombinedDatabaseCreditPack,
    setSelectedCombinedDatabaseCreditPack,
  ] = useState('');
  // Combined pack price IDs come from the live Stripe catalog; re-render once
  // the fetch resolves so the tier dropdown populates.
  const catalogReady = useCreditPackCatalog();

  const [
    selectedBusinessVerificationCredits,
    setSelectedBusinessVerificationCredits,
  ] = useState('');
  const businessVerificationPrice =
    process.env.REACT_APP_BUSINESS_VERIFICATION_CREDIT_PRICE || '';
  const [analyzerRefreshCredits, setAnalyzerRefreshCredits] = useState('');
  // Database Records (Direct Records) -- manual set of db_size.
  const [databaseRowsToAdd, setDatabaseRowsToAdd] = useState('');
  const [isSetDatabaseRowsModalOpen, setIsSetDatabaseRowsModalOpen] =
    useState(false);
  const [isSettingDatabaseRows, setIsSettingDatabaseRows] = useState(false);
  const [verificationCreditsToSet, setVerificationCreditsToSet] = useState('');
  // The team's live Business Verification balance (team_credits), which a
  // manual set changes independently of the subscription's quantity.
  const [verificationSummary, setVerificationSummary] = useState<{
    total: number;
    used: number;
  } | null>(null);
  const loadVerificationSummary = useCallback(async () => {
    if (!curTeam?.id) return;
    const response = await CreditsAPI.getAdminVerificationCreditsSummary(
      String(curTeam.id),
    );
    setVerificationSummary(
      response?.success
        ? {
            total: Number(response.result?.verification_credits ?? 0),
            used: Number(response.result?.used_this_month ?? 0),
          }
        : null,
    );
  }, [curTeam?.id]);
  useEffect(() => {
    loadVerificationSummary();
  }, [loadVerificationSummary]);
  const [isSetVerificationModalOpen, setIsSetVerificationModalOpen] =
    useState(false);
  const [isSettingVerification, setIsSettingVerification] = useState(false);
  const [verificationAddBack, setVerificationAddBack] = useState('');
  const [isAddBackModalOpen, setIsAddBackModalOpen] = useState(false);
  const [isAddingBack, setIsAddingBack] = useState(false);
  // Unlimited workflows (team_credits.unlimited_workflows) -- loaded per team,
  // saved immediately on toggle.
  const [unlimitedWorkflows, setUnlimitedWorkflows] = useState(false);
  const [isSavingUnlimitedWorkflows, setIsSavingUnlimitedWorkflows] =
    useState(false);
  // Workflow Credits (Direct Credits) -- writes to Workflows' own pool
  // (team_credits.workflow_credits) via addWorkflowCredits.
  const [workflowCreditsToAdd, setWorkflowCreditsToAdd] = useState('');
  const [isAddWorkflowCreditsModalOpen, setIsAddWorkflowCreditsModalOpen] =
    useState(false);
  const [isAddingWorkflowCredits, setIsAddingWorkflowCredits] = useState(false);
  // Analyzers Credits (Direct Credits) -- the Bulk Tariff Analyzer pack, the
  // historical pool (team_credits.monthly_credits) via addCredits.
  const [analyzersCreditsToAdd, setAnalyzersCreditsToAdd] = useState('');
  // The team's live analyzer & workflow balance, shown in the confirmation so
  // the admin sees the before/after of the adjustment.
  const [analyzersSummary, setAnalyzersSummary] = useState<{
    total: number;
    remaining: number;
  } | null>(null);
  const loadAnalyzersSummary = useCallback(async () => {
    if (!curTeam?.id) return;
    const response = await CreditsAPI.getAdminUsageBreakdown(
      String(curTeam.id),
    );
    const paid = response?.result?.paid;
    setAnalyzersSummary(
      response?.success
        ? {
            total: Number(paid?.total_monthly_credits ?? 0),
            remaining: Number(paid?.remaining_credits ?? 0),
          }
        : null,
    );
  }, [curTeam?.id]);
  // The team's live database capacity and records in use, shown in the
  // database-rows confirmation so the admin sees the before/after of the change.
  const [databaseSummary, setDatabaseSummary] = useState<{
    total: number;
    used: number;
  } | null>(null);
  const loadDatabaseSummary = useCallback(async () => {
    if (!curTeam?.id) return;
    const teamId = String(curTeam.id);
    const [breakdown, usage] = await Promise.all([
      CreditsAPI.getAdminUsageBreakdown(teamId),
      CreditsAPI.getAdminDatabaseUsage(teamId),
    ]);
    setDatabaseSummary(
      breakdown?.success && usage?.success
        ? {
            total: Number(breakdown.result?.paid?.db_size ?? 0),
            used:
              Number(usage.result?.entity ?? 0) +
              Number(usage.result?.product ?? 0) +
              Number(usage.result?.exportProduct ?? 0),
          }
        : null,
    );
  }, [curTeam?.id]);
  const [isAddAnalyzersCreditsModalOpen, setIsAddAnalyzersCreditsModalOpen] =
    useState(false);
  const [isAddingAnalyzersCredits, setIsAddingAnalyzersCredits] =
    useState(false);
  const [isCreatingSubscriptions, setIsCreatingSubscriptions] = useState(false);
  const [isCreatingAnalyzerRefresh, setIsCreatingAnalyzerRefresh] =
    useState(false);
  const [isEntityScreenAddonEnabled, setIsEntityScreenAddonEnabled] =
    useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [billingHidden, setBillingHidden] = useState(false);
  const [savingBillingHidden, setSavingBillingHidden] = useState(false);

  useEffect(() => {
    setBillingHidden(curTeam?.attributes?.billing_hidden?.[0] === 'true');
  }, [curTeam?.attributes?.billing_hidden]);

  const toggleBillingHidden = async (hidden: boolean) => {
    setSavingBillingHidden(true);
    const res = await GroupAPI.setBillingHidden(curTeam.id, hidden);
    setSavingBillingHidden(false);
    if (res?.success) {
      setBillingHidden(hidden);
      enqueueSnackbar(
        hidden
          ? 'Billing tab hidden for this team'
          : 'Billing tab shown for this team',
        { variant: 'success' },
      );
    } else {
      enqueueSnackbar(`Error updating billing tab visibility: ${res?.result}`, {
        variant: 'error',
      });
    }
  };

  const initializedFromActiveRef = useRef(false);

  const stripeDashboardBaseUrl =
    process.env.REACT_APP_STRIPE_DASHBOARD_BASE_URL || '';

  const standardPrice = process.env.REACT_APP_STANDARD_PRICE || '';
  const proPrice = process.env.REACT_APP_PRO_PRICE || '';
  const businessPrice = process.env.REACT_APP_BUSINESS_PRICE || '';
  const analyzerRefreshPrice =
    process.env.REACT_APP_ANALYZER_REFRESH_PRICE || '';
  const enterpriseStarterPrice =
    process.env.REACT_APP_ENTERPRISE_STARTER_PRICE || '';
  const enterpriseGrowthPrice =
    process.env.REACT_APP_ENTERPRISE_GROWTH_PRICE || '';
  const enterpriseScalePrice =
    process.env.REACT_APP_ENTERPRISE_SCALE_PRICE || '';
  const enterprisePlusPrice = process.env.REACT_APP_ENTERPRISE_PLUS_PRICE || '';
  const enterpriseMaxPrice = process.env.REACT_APP_ENTERPRISE_MAX_PRICE || '';

  // entity add-on role
  const entityAddonId = process.env.REACT_APP_ENTITY_ADDON_ID || '';

  useEffect(() => {
    let isMounted = true;
    setUnlimitedWorkflows(false);
    if (!curTeam?.id) return undefined;
    CreditsAPI.getUnlimitedWorkflows(curTeam.id).then((res) => {
      if (isMounted && res?.success) {
        setUnlimitedWorkflows(Boolean(res.result?.unlimited_workflows));
      }
    });
    return () => {
      isMounted = false;
    };
  }, [curTeam?.id]);

  useEffect(() => {
    let isMounted = true;
    const checkEntityAddonStatus = async () => {
      if (!curTeam?.id) return;
      try {
        const res = await GroupAPI.getGroupRealmRoles(curTeam.id);
        if (res?.success && Array.isArray(res.result)) {
          const hasAddonRole = res.result.some(
            (role: any) =>
              role.name === 'entity-addon' || role.id === entityAddonId,
          );
          if (isMounted) {
            setIsEntityScreenAddonEnabled(hasAddonRole);
          }
        }
      } catch (error) {
        console.error('Failed to fetch team roles:', error);
      }
    };
    checkEntityAddonStatus();
    return () => {
      isMounted = false;
    };
  }, [curTeam?.id, entityAddonId]);

  const productConfigs: ProductConfig[] = [
    {
      id: 'standard',
      moduleName: 'BITE Standard',
      label: 'Standard',
      price: 5,
      priceId: standardPrice,
      type: 'fixed',
    },
    {
      id: 'pro',
      moduleName: 'BITE Pro',
      label: 'Pro',
      price: 64,
      priceId: proPrice,
      type: 'fixed',
    },
    {
      id: 'business',
      moduleName: 'BITE Business',
      label: 'Business',
      price: 99,
      priceId: businessPrice,
      type: 'fixed',
    },
  ];

  const enterpriseConfigs: EnterpriseConfig[] = [
    {
      id: 'enterpriseStarter',
      moduleName: 'BITE Enterprise Starter',
      label: 'Enterprise Starter',
      priceId: enterpriseStarterPrice,
      type: 'fixed',
      fixedQuantity: 50,
      fixedCredits: 300000,
    },
    {
      id: 'enterpriseGrowth',
      moduleName: 'BITE Enterprise Growth',
      label: 'Enterprise Growth',
      priceId: enterpriseGrowthPrice,
      type: 'fixed',
      fixedQuantity: 50,
      fixedCredits: 500000,
    },
    {
      id: 'enterpriseScale',
      moduleName: 'BITE Enterprise Scale',
      label: 'Enterprise Scale',
      priceId: enterpriseScalePrice,
      type: 'fixed',
      fixedQuantity: 50,
      fixedCredits: 1000000,
    },
    {
      id: 'enterprisePlus',
      moduleName: 'BITE Enterprise Plus',
      label: 'Enterprise Plus',
      priceId: enterprisePlusPrice,
      type: 'fixed',
      fixedQuantity: 50,
      fixedCredits: 2000000,
    },
    {
      id: 'enterpriseMax',
      moduleName: 'BITE Enterprise Max',
      label: 'Enterprise Max',
      priceId: enterpriseMaxPrice,
      type: 'fixed',
      fixedQuantity: 50,
      fixedCredits: 5000000,
    },
  ];

  // setSelectedEnterprisePlanId's only call site is inside the commented-out
  // Enterprise plan cards block below -- keeping the setter (rather than
  // dropping it) so re-enabling that block doesn't also require re-adding state.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedEnterprisePlanId, setSelectedEnterprisePlanId] = useState<
    string | null
  >(null);

  const activeSubscriptions =
    customerInfo?.data?.subscriptions?.data?.filter((subscription: any) =>
      ['active', 'trialing', 'past_due'].includes(subscription?.status),
    ) || [];

  const activeByProduct = useMemo(() => {
    const map: Record<string, any> = {};

    activeSubscriptions.forEach((subscription: any) => {
      const moduleName =
        subscription?.metadata?.moduleName?.toLowerCase?.() ||
        subscription?.items?.data?.[0]?.metadata?.moduleName?.toLowerCase?.() ||
        '';
      const subscriptionPriceId =
        subscription?.items?.data?.[0]?.price?.id || '';

      const licenseType = resolveLicenseTypeForItem(
        subscription?.items?.data?.[0],
        subscription,
      );
      if (licenseType === 'standard') {
        map.standard = subscription;
      }
      if (licenseType === 'pro') {
        map.pro = subscription;
      }
      if (licenseType === 'business') {
        map.business = subscription;
      }

      if (
        (businessVerificationPrice &&
          subscriptionPriceId === businessVerificationPrice) ||
        moduleName.includes('business verification')
      ) {
        map.businessVerification = subscription;
      }

      if (subscriptionPriceId === analyzerRefreshPrice) {
        map.analyzerRefresh = subscription;
      }

      if (subscriptionPriceId === enterpriseStarterPrice) {
        map.enterpriseStarter = subscription;
      }
      if (subscriptionPriceId === enterpriseGrowthPrice) {
        map.enterpriseGrowth = subscription;
      }
      if (subscriptionPriceId === enterpriseScalePrice) {
        map.enterpriseScale = subscription;
      }
      if (subscriptionPriceId === enterprisePlusPrice) {
        map.enterprisePlus = subscription;
      }
      if (subscriptionPriceId === enterpriseMaxPrice) {
        map.enterpriseMax = subscription;
      }

      const databaseCreditPriceIds = Object.values(databaseCreditPackPriceIds);
      const productDatabaseCreditPriceIds = Object.values(
        productDatabaseCreditPackPriceIds,
      );
      // The combined pack is named like a legacy "Database Records Pack N", so
      // it is told apart by its price: one from the live combined catalog, or
      // one that grants BOTH Entity and Product DB rows (legacy prices grant one).
      const subscriptionPriceMetadata =
        subscription?.items?.data?.[0]?.price?.metadata || {};
      const isCombinedDatabaseCreditPack =
        Object.values(databaseCombinedCreditPackPriceIds).includes(
          subscriptionPriceId,
        ) ||
        (Boolean(subscriptionPriceMetadata.db_size) &&
          Boolean(subscriptionPriceMetadata.product_db_size));
      if (isCombinedDatabaseCreditPack) {
        map.databaseCombinedCreditPack = subscription;
        return;
      }
      const packKind = legacyDatabasePackKind(moduleName);
      if (
        packKind === 'database' ||
        databaseCreditPriceIds.includes(subscriptionPriceId)
      ) {
        map.databaseCreditPack = subscription;
      }
      if (
        packKind === 'product' ||
        productDatabaseCreditPriceIds.includes(subscriptionPriceId)
      ) {
        map.productDatabaseCreditPack = subscription;
      }
    });

    return map;
  }, [
    activeSubscriptions,
    standardPrice,
    proPrice,
    businessPrice,
    analyzerRefreshPrice,
    businessVerificationPrice,
    enterpriseStarterPrice,
    enterpriseGrowthPrice,
    enterpriseScalePrice,
    enterprisePlusPrice,
    enterpriseMaxPrice,
    databaseCreditPackPriceIds,
    productDatabaseCreditPackPriceIds,
    databaseCombinedCreditPackPriceIds,
  ]);

  useEffect(() => {
    if (initializedFromActiveRef.current) {
      return;
    }

    const hasAnyActive =
      Boolean(activeByProduct.standard) ||
      Boolean(activeByProduct.pro) ||
      Boolean(activeByProduct.business) ||
      Boolean(activeByProduct.databaseCreditPack) ||
      Boolean(activeByProduct.productDatabaseCreditPack);

    if (!hasAnyActive) {
      return;
    }

    setQuantities((prev) => ({
      standard:
        prev.standard ||
        Number(activeByProduct.standard?.items?.data?.[0]?.quantity || 0),
      pro:
        prev.pro ||
        Number(activeByProduct.pro?.items?.data?.[0]?.quantity || 0),
      business:
        prev.business ||
        Number(activeByProduct.business?.items?.data?.[0]?.quantity || 0),
    }));

    initializedFromActiveRef.current = true;
  }, [activeByProduct]);

  const initialQuantities = useMemo(
    () => ({
      standard: Number(
        activeByProduct.standard?.items?.data?.[0]?.quantity || 0,
      ),
      pro: Number(activeByProduct.pro?.items?.data?.[0]?.quantity || 0),
      business: Number(
        activeByProduct.business?.items?.data?.[0]?.quantity || 0,
      ),
    }),
    [activeByProduct],
  );

  const stripeVerificationQuantity = Number(
    activeByProduct.businessVerification?.items?.data?.[0]?.quantity || 0,
  );

  const initialDatabaseCreditPackSelection = useMemo(() => {
    const activeDatabaseCredit = activeByProduct.databaseCreditPack;
    if (!activeDatabaseCredit) return '';

    const moduleName = activeDatabaseCredit?.metadata?.moduleName || '';
    const match = moduleName.match(/(\d[\d,]*)/);
    if (match?.[1]) {
      return match[1].replace(/,/g, '');
    }

    const activePriceId =
      activeDatabaseCredit?.items?.data?.[0]?.price?.id || '';
    const matchedCredits = Object.entries(databaseCreditPackPriceIds).find(
      ([, priceId]) => priceId === activePriceId,
    )?.[0];

    return matchedCredits || String(databaseCreditPacks[0]?.credits || 200);
  }, [activeByProduct.databaseCreditPack]);

  const initialProductDatabaseCreditPackSelection = useMemo(() => {
    const activeProductDatabaseCredit =
      activeByProduct.productDatabaseCreditPack;
    if (!activeProductDatabaseCredit) return '';

    const moduleName = activeProductDatabaseCredit?.metadata?.moduleName || '';
    const match = moduleName.match(/(\d[\d,]*)/);
    if (match?.[1]) {
      return match[1].replace(/,/g, '');
    }

    const activePriceId =
      activeProductDatabaseCredit?.items?.data?.[0]?.price?.id || '';
    const matchedCredits = Object.entries(
      productDatabaseCreditPackPriceIds,
    ).find(([, priceId]) => priceId === activePriceId)?.[0];

    return (
      matchedCredits || String(productDatabaseCreditPacks[0]?.credits || 200)
    );
  }, [
    activeByProduct.productDatabaseCreditPack,
    productDatabaseCreditPackPriceIds,
  ]);

  useEffect(() => {
    if (activeByProduct.databaseCreditPack) {
      setSelectedDatabaseCreditPack(initialDatabaseCreditPackSelection);
    }
  }, [activeByProduct.databaseCreditPack, initialDatabaseCreditPackSelection]);
  useEffect(() => {
    if (activeByProduct.productDatabaseCreditPack) {
      setSelectedProductDatabaseCreditPack(
        initialProductDatabaseCreditPackSelection,
      );
    }
  }, [
    activeByProduct.productDatabaseCreditPack,
    initialProductDatabaseCreditPackSelection,
  ]);

  // Only tiers that exist in the live combined-pack Stripe catalog.
  const availableCombinedDatabaseCreditPacks = databaseCreditPacks.filter(
    (pack) => Boolean(databaseCombinedCreditPackPriceIds[pack.credits]),
  );

  // Show the active tier in the (disabled) dropdown once a combined pack exists.
  useEffect(() => {
    const activePriceId =
      activeByProduct.databaseCombinedCreditPack?.items?.data?.[0]?.price?.id;
    if (!activePriceId) return;
    const activeCredits = Object.entries(
      databaseCombinedCreditPackPriceIds,
    ).find(([, priceId]) => priceId === activePriceId)?.[0];
    if (activeCredits) setSelectedCombinedDatabaseCreditPack(activeCredits);
  }, [activeByProduct.databaseCombinedCreditPack, catalogReady]);

  const combinedDatabaseCreditPackSelectionChanged =
    selectedCombinedDatabaseCreditPack !== '' &&
    !activeByProduct.databaseCombinedCreditPack;

  // Legacy packs are display-only: make sure the active tier is always an
  // option even if it's no longer in the standard tier list.
  const withSelectedPack = (
    packs: { credits: number; price: number }[],
    selected: string,
  ) =>
    !selected || packs.some((pack) => String(pack.credits) === selected)
      ? packs
      : [{ credits: Number(selected), price: 0 }, ...packs];
  const availableDatabaseCreditPacks = withSelectedPack(
    databaseCreditPacks,
    selectedDatabaseCreditPack,
  );
  const availableProductDatabaseCreditPacks = withSelectedPack(
    productDatabaseCreditPacks,
    selectedProductDatabaseCreditPack,
  );

  const hasLegacyDatabaseCreditPack =
    !activeByProduct.databaseCombinedCreditPack &&
    (Boolean(activeByProduct.databaseCreditPack) ||
      Boolean(activeByProduct.productDatabaseCreditPack));

  // Bugfix: Add checks for empty strings so changes are only active if a pack is genuinely selected
  const databaseCreditPackSelectionChanged =
    selectedDatabaseCreditPack !== '' &&
    selectedDatabaseCreditPack !== initialDatabaseCreditPackSelection &&
    !activeByProduct.databaseCreditPack;

  const productDatabaseCreditPackSelectionChanged =
    selectedProductDatabaseCreditPack !== '' &&
    selectedProductDatabaseCreditPack !==
      initialProductDatabaseCreditPackSelection &&
    !activeByProduct.productDatabaseCreditPack;

  const businessVerificationSelectionChanged =
    selectedBusinessVerificationCredits !== '' &&
    !activeByProduct.businessVerification;

  const hasPendingChanges =
    businessVerificationSelectionChanged ||
    Boolean(selectedEnterprisePlanId) ||
    quantities.standard !== initialQuantities.standard ||
    quantities.pro !== initialQuantities.pro ||
    quantities.business !== initialQuantities.business ||
    databaseCreditPackSelectionChanged ||
    productDatabaseCreditPackSelectionChanged ||
    combinedDatabaseCreditPackSelectionChanged;

  const isEnterpriseSelectionActive = Boolean(selectedEnterprisePlanId);
  const selectedEnterprisePlan = enterpriseConfigs.find(
    (plan) => plan.id === selectedEnterprisePlanId,
  );

  const hasActiveEnterprisePlan = enterpriseConfigs.some((plan) =>
    Boolean(activeByProduct[plan.id]),
  );

  const isBaseAndCreditSelectionDisabled =
    isEnterpriseSelectionActive || hasActiveEnterprisePlan;

  const updateQuantity = (productId: string, delta: number) => {
    setQuantities((prev) => ({
      ...prev,
      [productId]: Math.max(0, (prev[productId] || 0) + delta),
    }));
  };

  const adminCreateSubscription = async (
    cus_id: string,
    selectedItems: SelectedSubscriptionItem[],
    includeCreditPacks?: Array<{
      credits: string;
      isDatabase: boolean;
      isProductDatabase?: boolean;
      isCombinedDatabase?: boolean;
      isBusinessVerification?: boolean;
    }>,
  ) => {
    if (!cus_id) {
      enqueueSnackbar('Unable to create subscriptions: customer missing.', {
        variant: 'error',
      });
      return;
    }

    const payloads: any[] = selectedItems.map(
      ({ product, quantity, freeTrialEnabled, freeTrialDays }) => ({
        lineItems: [
          {
            price: product.priceId,
            quantity: quantity,
            metadata: {
              type: product.type,
              moduleName: product.moduleName,
            },
          },
        ],
        metadata: {
          team_id: curTeam.id,
          keycloakUserId: adminUser?.data?.id,
          moduleName: product.moduleName,
        },
        freeTrialEnabled: Boolean(freeTrialEnabled),
        freeTrialDays: freeTrialEnabled
          ? freeTrialDays || DEFAULT_FREE_TRIAL_DAYS
          : 0,
      }),
    );

    if (includeCreditPacks?.length) {
      includeCreditPacks.forEach((creditPack) => {
        if (creditPack.isBusinessVerification) {
          payloads.push({
            lineItems: [
              {
                price: businessVerificationPrice,
                quantity: Number(creditPack.credits),
                metadata: {
                  type: 'business_verification_addon',
                  moduleName: 'Business Verification Add-on',
                },
              },
            ],
            metadata: {
              team_id: curTeam.id,
              keycloakUserId: adminUser?.data?.id,
              moduleName: 'Business Verification Add-on',
            },
          });
          return;
        }
        const creditPriceId = creditPack.isCombinedDatabase
          ? databaseCombinedCreditPackPriceIds[Number(creditPack.credits)]
          : creditPack.isProductDatabase
          ? productDatabaseCreditPackPriceIds[Number(creditPack.credits)]
          : databaseCreditPackPriceIds[Number(creditPack.credits)];
        const moduleName = creditPack.isProductDatabase
          ? `Product Database Records Pack ${creditPack.credits}`
          : `Database Records Pack ${creditPack.credits}`;

        payloads.push({
          lineItems: [
            {
              price: creditPriceId,
              quantity: 1,
              metadata: {
                type: 'credit',
                moduleName: moduleName,
              },
            },
          ],
          metadata: {
            team_id: curTeam.id,
            keycloakUserId: adminUser?.data?.id,
            moduleName: moduleName,
          },
        });
      });
    }

    if (payloads.length === 0) {
      enqueueSnackbar(
        'Please select at least one subscription or credit pack.',
        {
          variant: 'warning',
        },
      );
      return;
    }

    // A combined Database Records pack replaces the legacy Entity / Product
    // packs, so they are cancelled in Stripe BEFORE the combined pack is bought
    // (a failure here stops the save before anything is bought).
    const combinedPackIndex = includeCreditPacks?.findIndex(
      (creditPack) => creditPack.isCombinedDatabase,
    );
    const hasCombinedPack =
      combinedPackIndex !== undefined && combinedPackIndex >= 0;
    const legacySubIds = hasCombinedPack
      ? ([
          activeByProduct.databaseCreditPack?.id,
          activeByProduct.productDatabaseCreditPack?.id,
        ].filter(Boolean) as string[])
      : [];
    let legacyCreditLineIds: string[] = [];
    if (legacySubIds.length) {
      const combinedPriceId =
        databaseCombinedCreditPackPriceIds[
          Number(includeCreditPacks?.[combinedPackIndex as number]?.credits)
        ];
      if (!combinedPriceId) {
        enqueueSnackbar(
          'The combined Database Records price is not available, so the legacy packs were not cancelled.',
          { variant: 'error' },
        );
        return;
      }
      const cancelResult =
        await PaymentsAPI.cancelLegacyDatabaseCreditSubscriptions(cus_id, {
          legacySubIds: legacySubIds,
        });
      if (!cancelResult?.success) {
        enqueueSnackbar(
          `The legacy database records packs could not be cancelled, so nothing was purchased. ${
            typeof cancelResult?.result === 'string' ? cancelResult.result : ''
          }`.trim(),
          { variant: 'error' },
        );
        return;
      }
      const {
        canceledSubIds = [],
        creditLineIds = [],
        error: cancelError,
      } = cancelResult.result ?? {};
      legacyCreditLineIds = creditLineIds;
      if (cancelError) {
        // Stripe stopped partway. The packs it cancelled are gone, so say
        // which, and show the subscriptions as they now are.
        const packNames = (subIds: string[]) =>
          subIds
            .map((subId) =>
              subId === activeByProduct.productDatabaseCreditPack?.id
                ? 'Product Database records pack'
                : 'Entity Database records pack',
            )
            .join(' and ');
        const cancelled = legacySubIds.filter((id) =>
          canceledSubIds.includes(id),
        );
        const notCancelled = legacySubIds.filter(
          (id) => !canceledSubIds.includes(id),
        );
        enqueueSnackbar(
          cancelled.length
            ? `The ${packNames(cancelled)} was cancelled, but the ${packNames(
                notCancelled,
              )} could not be (${cancelError}), so nothing was purchased. Save again to finish: the cancelled pack's credit will go on the combined pack.`
            : `The legacy database records packs could not be cancelled, so nothing was purchased. ${cancelError}`,
          { variant: 'error' },
        );
        if (cancelled.length) await onSubscriptionsChanged?.();
        return;
      }
    }

    const results = await Promise.all(
      payloads.map((payload) =>
        PaymentsAPI.adminCreateSubscription(cus_id, payload),
      ),
    );

    const hasSuccessfulProOrBusinessTrial = selectedItems.some(
      ({ product, freeTrialEnabled }, index) =>
        Boolean(freeTrialEnabled) &&
        ['pro', 'business'].includes(product.id) &&
        Boolean(results[index]?.success),
    );

    if (hasSuccessfulProOrBusinessTrial && curTeam?.id) {
      const seedResponse = await CreditsAPI.seedTrialCredits({
        teamId: curTeam.id,
      });

      if (!seedResponse?.success) {
        enqueueSnackbar(
          seedResponse?.result ||
            'A trial subscription was created, but trial credits could not be initialized.',
          {
            variant: 'warning',
          },
        );
      }
    }

    const combinedPackCreated =
      hasCombinedPack &&
      Boolean(results[selectedItems.length + (combinedPackIndex as number)]?.success);

    let legacyCancelFailed = false;
    // Always attach once the combined pack exists: the server also attaches
    // credit an earlier save left pending (it stopped after a cancel, or the
    // page was reloaded), which this save never saw.
    if (combinedPackCreated) {
      const combinedSubId =
        results[selectedItems.length + (combinedPackIndex as number)]?.result
          ?.id;
      const attachResult = combinedSubId
        ? await PaymentsAPI.attachLegacyCreditToSubscription(cus_id, {
            combinedSubId: combinedSubId,
            creditLineIds: legacyCreditLineIds,
          })
        : { success: false };
      if (!attachResult?.success) {
        legacyCancelFailed = true;
        enqueueSnackbar(
          'The combined pack was created, but prorated credit from the cancelled legacy packs could not be attached to it. Apply any pending credit manually in Stripe.',
          { variant: 'warning' },
        );
      }
    } else if (hasCombinedPack && legacySubIds.length && !combinedPackCreated) {
      enqueueSnackbar(
        'The legacy packs were cancelled but the combined pack could not be created. Select the combined pack and save again.',
        { variant: 'error' },
      );
    }

    const outcome = subscriptionSaveOutcome(results, legacyCancelFailed);
    if (outcome === 'reload') {
      history.go(0);
      return;
    }
    if (!legacyCancelFailed) {
      enqueueSnackbar(
        'One or more subscriptions failed to create. Verify pricing configuration and customer details.',
        { variant: 'error' },
      );
    }
    // Awaited while Save is still disabled, so it can't be pressed again
    // before what was just created shows as active.
    if (outcome === 'refresh') await onSubscriptionsChanged?.();
  };

  const handleCreateAnalyzerRefresh = async () => {
    if (isCreatingAnalyzerRefresh) {
      return;
    }
    const cus_id = customerInfo?.data?.id;
    if (!cus_id) {
      enqueueSnackbar('Unable to create subscription: customer missing.', {
        variant: 'error',
      });
      return;
    }
    if (!analyzerRefreshPrice) {
      enqueueSnackbar(
        'Analyzer Auto-Refresh price is not configured (REACT_APP_ANALYZER_REFRESH_PRICE).',
        { variant: 'error' },
      );
      return;
    }
    if (isAnalyzerRefreshInvalid) {
      return;
    }

    const cycledCredits = String(parsedAnalyzerRefreshCredits);
    const moduleName = 'Analyzer Auto-Refresh';
    const payload = {
      lineItems: [
        {
          price: analyzerRefreshPrice,
          quantity: 1,
          metadata: {
            type: 'credit',
            moduleName: moduleName,
            cycled_credits: cycledCredits,
          },
        },
      ],
      metadata: {
        team_id: curTeam.id,
        keycloakUserId: adminUser?.data?.id,
        moduleName: moduleName,
        cycled_credits: cycledCredits,
      },
    };

    setIsCreatingAnalyzerRefresh(true);
    try {
      const result = await PaymentsAPI.adminCreateSubscription(cus_id, payload);

      if (result?.success) {
        history.go(0);
        return;
      }

      enqueueSnackbar(
        result?.result ||
          'Failed to create the Analyzer Auto-Refresh subscription.',
        { variant: 'error' },
      );
    } finally {
      setIsCreatingAnalyzerRefresh(false);
    }
  };

  const handleViewInStripe = () => {
    const customerId = customerInfo?.data?.id;
    if (!stripeDashboardBaseUrl || !customerId) {
      return;
    }
    const normalizedBaseUrl = stripeDashboardBaseUrl.replace(/\/$/, '');
    window.open(
      `${normalizedBaseUrl}/customers/${customerId}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  const parsedAnalyzerRefreshCredits = Number(analyzerRefreshCredits);
  const isAnalyzerRefreshInvalid =
    !Number.isFinite(parsedAnalyzerRefreshCredits) ||
    parsedAnalyzerRefreshCredits <= 0 ||
    !Number.isInteger(parsedAnalyzerRefreshCredits);
  const parsedDatabaseRowsToAdd = Number(databaseRowsToAdd);
  const isDatabaseRowsAmountInvalid =
    !Number.isFinite(parsedDatabaseRowsToAdd) ||
    parsedDatabaseRowsToAdd <= 0 ||
    !Number.isInteger(parsedDatabaseRowsToAdd);
  const parsedVerificationCreditsToSet = Number(verificationCreditsToSet);
  const isVerificationCreditsInvalid =
    !Number.isFinite(parsedVerificationCreditsToSet) ||
    parsedVerificationCreditsToSet < 0 ||
    !Number.isInteger(parsedVerificationCreditsToSet) ||
    verificationCreditsToSet.trim() === '';
  const parsedWorkflowCreditsToAdd = Number(workflowCreditsToAdd);
  const isWorkflowCreditsAmountInvalid =
    !Number.isFinite(parsedWorkflowCreditsToAdd) ||
    parsedWorkflowCreditsToAdd <= 0 ||
    !Number.isInteger(parsedWorkflowCreditsToAdd);
  const parsedAnalyzersCreditsToAdd = Number(analyzersCreditsToAdd);
  const isAnalyzersCreditsAmountInvalid =
    !Number.isFinite(parsedAnalyzersCreditsToAdd) ||
    parsedAnalyzersCreditsToAdd <= 0 ||
    !Number.isInteger(parsedAnalyzersCreditsToAdd);

  const handleOpenSetDatabaseRowsModal = () => {
    if (isDatabaseRowsAmountInvalid) {
      enqueueSnackbar('Please enter a valid whole number of database rows.', {
        variant: 'warning',
      });
      return;
    }

    setDatabaseSummary(null);
    loadDatabaseSummary();
    setIsSetDatabaseRowsModalOpen(true);
  };

  const handleConfirmSetDatabaseRows = async () => {
    if (!curTeam?.id || isDatabaseRowsAmountInvalid) {
      return;
    }

    setIsSettingDatabaseRows(true);
    const response = await CreditsAPI.setDbSize({
      teamId: curTeam.id,
      amount: parsedDatabaseRowsToAdd,
      field: 'db_size',
    });
    setIsSettingDatabaseRows(false);

    if (response?.success) {
      enqueueSnackbar(
        `Database records set to ${parsedDatabaseRowsToAdd.toLocaleString()} for ${
          curTeam?.name
        }.`,
        { variant: 'success' },
      );
      setDatabaseRowsToAdd('');
      setIsSetDatabaseRowsModalOpen(false);
      loadDatabaseSummary();
      return;
    }

    enqueueSnackbar(response?.result || 'Failed to set database records.', {
      variant: 'error',
    });
  };

  const parsedVerificationAddBack = Number(verificationAddBack);
  const verificationSpent = verificationSummary?.used ?? 0;
  const isVerificationAddBackInvalid =
    verificationAddBack.trim() === '' ||
    !Number.isInteger(parsedVerificationAddBack) ||
    parsedVerificationAddBack <= 0 ||
    parsedVerificationAddBack > verificationSpent;

  const handleConfirmAddBackVerificationCredits = async () => {
    if (!curTeam?.id || isVerificationAddBackInvalid) {
      return;
    }

    setIsAddingBack(true);
    const response = await CreditsAPI.addBackVerificationCredits({
      teamId: curTeam.id,
      credits: parsedVerificationAddBack,
    });
    setIsAddingBack(false);

    if (response?.success) {
      enqueueSnackbar(
        `Added ${parsedVerificationAddBack.toLocaleString()} Business Verification credits back to ${
          curTeam?.name
        }.`,
        { variant: 'success' },
      );
      setVerificationAddBack('');
      setIsAddBackModalOpen(false);
      loadVerificationSummary();
      return;
    }

    enqueueSnackbar(
      response?.result || 'Failed to add Business Verification credits back.',
      { variant: 'error' },
    );
  };

  const handleConfirmSetVerificationCredits = async () => {
    if (!curTeam?.id || isVerificationCreditsInvalid) {
      return;
    }

    setIsSettingVerification(true);
    const response = await CreditsAPI.setDbSize({
      teamId: curTeam.id,
      amount: parsedVerificationCreditsToSet,
      field: 'verification_credits',
    });
    setIsSettingVerification(false);

    if (response?.success) {
      enqueueSnackbar(
        `Business Verification credits set to ${parsedVerificationCreditsToSet.toLocaleString()} for ${
          curTeam?.name
        }.`,
        { variant: 'success' },
      );
      setVerificationCreditsToSet('');
      setIsSetVerificationModalOpen(false);
      loadVerificationSummary();
      return;
    }

    enqueueSnackbar(
      response?.result || 'Failed to set Business Verification credits.',
      { variant: 'error' },
    );
  };

  const handleToggleUnlimitedWorkflows = async (enabled: boolean) => {
    if (!curTeam?.id || isSavingUnlimitedWorkflows) {
      return;
    }

    setIsSavingUnlimitedWorkflows(true);
    const response = await CreditsAPI.setUnlimitedWorkflows({
      teamId: curTeam.id,
      enabled: enabled,
    });
    setIsSavingUnlimitedWorkflows(false);

    if (response?.success) {
      setUnlimitedWorkflows(enabled);
      enqueueSnackbar(
        `Unlimited workflows ${enabled ? 'enabled' : 'disabled'} for ${
          curTeam?.name
        }.`,
        { variant: 'success' },
      );
      return;
    }

    enqueueSnackbar(
      response?.result || 'Failed to update unlimited workflows.',
      {
        variant: 'error',
      },
    );
  };

  const handleConfirmAddWorkflowCredits = async () => {
    if (!curTeam?.id || isWorkflowCreditsAmountInvalid) {
      return;
    }

    setIsAddingWorkflowCredits(true);
    const response = await CreditsAPI.addWorkflowCredits({
      teamId: curTeam.id,
      amount: parsedWorkflowCreditsToAdd,
    });
    setIsAddingWorkflowCredits(false);

    if (response?.success) {
      enqueueSnackbar(
        `${parsedWorkflowCreditsToAdd.toLocaleString()} workflow credits added to ${
          curTeam?.name
        }.`,
        { variant: 'success' },
      );
      setWorkflowCreditsToAdd('');
      setIsAddWorkflowCreditsModalOpen(false);
      return;
    }

    enqueueSnackbar(response?.result || 'Failed to add workflow credits.', {
      variant: 'error',
    });
  };

  const handleOpenAddAnalyzersCreditsModal = () => {
    if (isAnalyzersCreditsAmountInvalid) {
      enqueueSnackbar('Please enter a valid whole number of credits.', {
        variant: 'warning',
      });
      return;
    }

    setAnalyzersSummary(null);
    loadAnalyzersSummary();
    setIsAddAnalyzersCreditsModalOpen(true);
  };

  // "Analyzers & Workflow Credits" card -- grants into Analyzer & Workflow own pool
  // (team_credits.monthly_credits, the historical balance matching the
  // "Analyzer & Workflow Credits" Stripe product's own name) via plain addCredits.
  const handleConfirmAddAnalyzersCredits = async () => {
    if (!curTeam?.id || isAnalyzersCreditsAmountInvalid) {
      return;
    }

    setIsAddingAnalyzersCredits(true);
    const response = await CreditsAPI.addCredits({
      teamId: curTeam.id,
      amount: parsedAnalyzersCreditsToAdd,
    });
    setIsAddingAnalyzersCredits(false);

    if (response?.success) {
      enqueueSnackbar(
        `${parsedAnalyzersCreditsToAdd.toLocaleString()} analyzer & workflow credits added to ${
          curTeam?.name
        }.`,
        { variant: 'success' },
      );
      setAnalyzersCreditsToAdd('');
      setIsAddAnalyzersCreditsModalOpen(false);
      loadAnalyzersSummary();
      return;
    }

    enqueueSnackbar(
      response?.result || 'Failed to add analyzer & workflow credits.',
      {
        variant: 'error',
      },
    );
  };

  const styles: Record<string, CSSProperties> = {
    header: {
      fontSize: '24px',
      fontWeight: '600',
      marginBottom: '10px',
      color: 'var(--text-primary-color)',
    },
    subheader: { fontSize: '16px', color: 'var(--text-secondary)' },
    activeList: {
      marginTop: '20px',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      width: '100%',
    },
    cardsRow: {
      marginTop: '16px',
      display: 'flex',
      flexWrap: 'wrap',
      gap: '12px',
      alignItems: 'stretch',
    },
    controlsGrid: {
      marginTop: '12px',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '12px',
    },
    groupHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginTop: '18px',
      fontSize: '13px',
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      color: 'var(--text-tertiary)',
    },
    card: {
      flex: '1 1 260px',
      border: '1px solid var(--es-soft-border)',
      borderRadius: '12px',
      padding: '14px',
      background: 'var(--Background-200, var(--bg-colors))',
      boxShadow: 'var(--es-card-shadow)',
      color: 'var(--text-primary-color)',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
    },
    activeCard: {
      border: '1px solid var(--full-bar-color)',
      boxShadow: '0 0 0 1px rgba(22,163,74,0.45)',
      background: 'rgba(22,163,74,0.08)',
    },
    activeTag: {
      alignSelf: 'flex-start',
      background: 'var(--full-bar-color)',
      color: '#fff',
      borderRadius: '999px',
      padding: '2px 8px',
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.03em',
      textTransform: 'uppercase',
    },
    legacyTag: {
      alignSelf: 'flex-start',
      background: 'var(--mid-color)',
      color: '#fff',
      borderRadius: '999px',
      padding: '2px 8px',
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.03em',
      textTransform: 'uppercase',
    },
    tagRow: { display: 'flex', gap: '6px', alignItems: 'center' },
    cardTitleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flexWrap: 'wrap',
    },
    cardTitle: {
      fontSize: '18px',
      fontWeight: 600,
      color: 'var(--text-primary-color)',
    },
    cardPrice: { fontSize: '14px', color: 'var(--text-secondary)' },
    qtyRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '10px',
      marginTop: '4px',
    },
    qtyControls: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    enterpriseCreditsControls: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginTop: '8px',
    },
    stackedControl: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      marginTop: 'auto',
      paddingTop: '8px',
    },
    qtyBtn: {
      border: '1px solid var(--es-soft-border)',
      borderRadius: '8px',
      width: '30px',
      height: '30px',
      background: 'var(--bg-surface)',
      color: 'var(--text-primary-color)',
      cursor: 'pointer',
    },
    qtyValue: { minWidth: '22px', textAlign: 'center' },
    actionsRow: {
      marginTop: '16px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '10px',
      flexWrap: 'wrap',
    },
    viewStripeButton: {
      borderRadius: '8px',
      fontWeight: 600,
      fontSize: '15px',
      textTransform: 'none',
    },
    sectionHeader: {
      width: '100%',
      fontSize: '18px',
      fontWeight: 600,
      marginTop: '20px',
      color: 'var(--text-primary-color)',
      paddingBottom: '8px',
      borderBottom: '1px solid var(--es-soft-border)',
    },
    sectionSubtitle: {
      fontSize: '13px',
      fontWeight: 400,
      color: 'var(--text-secondary)',
      marginTop: '4px',
    },
    warningBox: {
      width: '100%',
      boxSizing: 'border-box',
      marginTop: '12px',
      padding: '12px 14px',
      borderRadius: '8px',
      fontSize: '13px',
      lineHeight: 1.5,
      color: 'var(--text-primary-color)',
      background: 'rgba(245, 158, 11, 0.12)',
      border: '1px solid var(--mid-color)',
    },
    sectionDescription: {
      width: '100%',
      fontSize: '13px',
      color: 'var(--text-secondary)',
      marginTop: '8px',
    },
    subHeader: {
      width: '100%',
      fontSize: '15px',
      fontWeight: 600,
      marginTop: '16px',
      color: 'var(--text-primary-color)',
    },
    detailRow: {
      fontSize: '13px',
      color: 'var(--text-secondary)',
    },
    trialToggleRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '10px',
      fontSize: '13px',
    },
    trialToggleLabel: {
      fontWeight: 600,
      color: 'var(--text-primary-color)',
    },
    trialToggle: {
      position: 'relative',
      display: 'inline-flex',
      width: '108px',
      height: '30px',
      borderRadius: '999px',
      border: '1px solid var(--es-soft-border)',
      background: 'var(--bg-surface)',
      cursor: 'pointer',
      overflow: 'hidden',
      padding: '2px',
    },
    trialToggleSlider: {
      position: 'absolute',
      top: '2px',
      bottom: '2px',
      width: '50%',
      borderRadius: '999px',
      background: 'var(--Background-300, var(--bg-secondary))',
      transition: 'transform 0.2s ease',
      transform: 'translateX(0%)',
    },
    trialToggleText: {
      position: 'relative',
      zIndex: 1,
      width: '50%',
      fontSize: '11px',
      fontWeight: 600,
      color: 'var(--text-secondary)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    selectBtn: {
      border: '1px solid var(--es-soft-border)',
      borderRadius: '8px',
      padding: '8px 12px',
      background: 'var(--bg-surface)',
      color: 'var(--text-primary-color)',
      cursor: 'pointer',
      fontWeight: 600,
      flexShrink: 0,
      whiteSpace: 'nowrap',
    },
    selectBtnRow: {
      display: 'flex',
      gap: '8px',
    },
    dangerSelectBtn: {
      border: '1px solid var(--low-color)',
      borderRadius: '8px',
      padding: '8px 12px',
      background: 'var(--bg-surface)',
      color: 'var(--low-color)',
      cursor: 'pointer',
      fontWeight: 600,
      flexShrink: 0,
      whiteSpace: 'nowrap',
      flex: '1 1 0',
    },
    disabledCard: {
      opacity: 0.55,
      pointerEvents: 'none',
    },
    createBtnContent: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
    },
  };

  return (
    <div className='super-admin-edit-access'>
      <div style={styles.header}>Create Custom Subscription</div>
      <div className='profile-basics'>
        <div className='profile-section-card'>
          <div className='profile-card-inner'>
            <div className='profile-card-primary'>Team Name</div>
            <div className='profile-card-secondary'>{curTeam?.name}</div>
          </div>
        </div>
        <div className='profile-section-card'>
          <div className='profile-card-inner'>
            <div className='profile-card-primary'>Team Admin</div>
            <div className='profile-card-secondary'>
              {curTeam?.attributes?.team_admin?.[0]}
            </div>
          </div>
        </div>
      </div>

      <div style={styles.sectionHeader}>Billing Tab</div>
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginTop: '12px',
          cursor: savingBillingHidden ? 'not-allowed' : 'pointer',
        }}
      >
        <input
          type='checkbox'
          checked={billingHidden}
          disabled={savingBillingHidden || !curTeam?.id}
          onChange={(e) => toggleBillingHidden(e.target.checked)}
        />
        Hide Billing tab (team pays by invoice)
      </label>

      <div style={styles.sectionHeader}>
        Subscription-Based Plans
        <div style={styles.sectionSubtitle}>
          Directly linked to application license tiers
        </div>
      </div>

      <div style={styles.cardsRow}>
        {productConfigs.map((product) => {
          const isActive = Boolean(activeByProduct[product.id]);
          const isDisabled = isBaseAndCreditSelectionDisabled;
          return (
            <div
              key={product.id}
              style={{
                ...styles.card,
                ...(isActive ? styles.activeCard : {}),
                ...(isDisabled ? styles.disabledCard : {}),
              }}
            >
              {isActive && <span style={styles.activeTag}>Active</span>}
              <div style={styles.cardTitleRow}>
            <div style={styles.cardTitle}>{product.label}</div>
            <PurchaseBadge kind='monthly' />
          </div>
              <div style={styles.cardPrice}>${product.price}/user/month</div>
              {!isActive && (
                <div style={styles.trialToggleRow}>
                  <span style={styles.trialToggleLabel}>Billing Mode</span>
                  <button
                    type='button'
                    style={styles.trialToggle}
                    onClick={() =>
                      setBasePlanTrialToggles((prev) => ({
                        ...prev,
                        [product.id]: !prev[product.id],
                      }))
                    }
                    disabled={isDisabled}
                    aria-label={`${product.label} billing mode`}
                  >
                    <span
                      style={{
                        ...styles.trialToggleSlider,
                        transform: basePlanTrialToggles[product.id]
                          ? 'translateX(96%)'
                          : 'translateX(0%)',
                      }}
                    />
                    <span style={styles.trialToggleText}>Default</span>
                    <span style={styles.trialToggleText}>Free Trial</span>
                  </button>
                </div>
              )}
              {!isActive && (
                <div style={styles.detailRow}>
                  {basePlanTrialToggles[product.id]
                    ? 'Creates a trial that auto-cancels at period end.'
                    : 'Creates the standard invoiced subscription.'}
                </div>
              )}
              {!isActive && basePlanTrialToggles[product.id] && (
                <label style={styles.trialToggleRow}>
                  <span style={styles.trialToggleLabel}>Trial duration</span>
                  <span>
                    <input
                      type='number'
                      min='1'
                      max={MAX_FREE_TRIAL_DAYS}
                      step='1'
                      value={basePlanTrialDays[product.id]}
                      onChange={(event) =>
                        setBasePlanTrialDays((prev) => ({
                          ...prev,
                          [product.id]: event.target.value,
                        }))
                      }
                      className='sa-trial-days-input'
                      disabled={isDisabled}
                      aria-label={`${product.label} free trial duration in days`}
                    />{' '}
                    days
                  </span>
                </label>
              )}
              <div style={styles.qtyRow}>
                <span>Quantity</span>
                {isActive ? (
                  <span style={styles.qtyValue}>
                    {quantities[product.id] || 0}
                  </span>
                ) : (
                  <div style={styles.qtyControls}>
                    <button
                      style={styles.qtyBtn}
                      onClick={() => updateQuantity(product.id, -1)}
                      type='button'
                      disabled={isDisabled}
                    >
                      -
                    </button>
                    <span style={styles.qtyValue}>
                      {quantities[product.id] || 0}
                    </span>
                    <button
                      style={styles.qtyBtn}
                      onClick={() => updateQuantity(product.id, 1)}
                      type='button'
                      disabled={isDisabled}
                    >
                      +
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div style={styles.subHeader}>Subscription-based credits</div>
      <div style={styles.sectionDescription}>
        These are credits based on the subscription / credit tiers in the
        application and are fixed and month to month. These values are what is
        stored in Stripe for the team account.
      </div>

      {hasLegacyDatabaseCreditPack && (
        <div style={styles.warningBox} role='alert'>
          <strong>Legacy database records packs detected.</strong> This team is
          still on the old Entity / Product Database Records packs. Please
          consider merging them into a single Database Records (combined) pack.
          Customers should be alerted of this change. Adding a combined pack
          below cancels the legacy packs immediately, with unused time credited
          (prorated) toward the combined pack.
        </div>
      )}

      <div style={styles.cardsRow}>
        <div
          style={{
            ...styles.card,
            ...(activeByProduct.databaseCombinedCreditPack
              ? styles.activeCard
              : {}),
            ...(isBaseAndCreditSelectionDisabled ? styles.disabledCard : {}),
          }}
        >
          {activeByProduct.databaseCombinedCreditPack && (
            <span style={styles.activeTag}>Active</span>
          )}
          <div style={styles.cardTitleRow}>
            <div style={styles.cardTitle}>Database Records (Combined)</div>
            <PurchaseBadge kind='monthly' />
          </div>
          <div style={styles.cardPrice}>
            Add monthly combined database records for both entity and product databases
          </div>
          <select
            className='sa-control-input'
            value={selectedCombinedDatabaseCreditPack}
            onChange={(e) =>
              setSelectedCombinedDatabaseCreditPack(e.target.value)
            }
            disabled={
              Boolean(activeByProduct.databaseCombinedCreditPack) ||
              isBaseAndCreditSelectionDisabled
            }
          >
            {!activeByProduct.databaseCombinedCreditPack && (
              <option value=''>None</option>
            )}
            {availableCombinedDatabaseCreditPacks.map((pack) => (
              <option
                key={`combined-db-${pack.credits}`}
                value={String(pack.credits)}
              >
                {pack.credits} records ({pack.credits} rows of data)
              </option>
            ))}
          </select>
        </div>
      </div>

      {hasLegacyDatabaseCreditPack && (
        <div style={styles.cardsRow}>
          {activeByProduct.databaseCreditPack && (
            <div
              style={{
                ...styles.card,
                ...styles.activeCard,
                ...(isBaseAndCreditSelectionDisabled
                  ? styles.disabledCard
                  : {}),
              }}
            >
              <div style={styles.tagRow}>
                <span style={styles.activeTag}>Active</span>
                <span style={styles.legacyTag}>Legacy</span>
              </div>
              <div style={styles.cardTitleRow}>
            <div style={styles.cardTitle}>Entity Database Records</div>
            <PurchaseBadge kind='monthly' />
          </div>
              <div style={styles.cardPrice}>
                Add monthly database records
              </div>
              <select
                className='sa-control-input'
                value={selectedDatabaseCreditPack}
                disabled
              >
                {availableDatabaseCreditPacks.map((pack) => (
                  <option
                    key={`db-${pack.credits}`}
                    value={String(pack.credits)}
                  >
                    {pack.credits} records ({pack.credits} rows of data)
                  </option>
                ))}
              </select>
            </div>
          )}
          {activeByProduct.productDatabaseCreditPack && (
            <div
              style={{
                ...styles.card,
                ...styles.activeCard,
                ...(isBaseAndCreditSelectionDisabled
                  ? styles.disabledCard
                  : {}),
              }}
            >
              <div style={styles.tagRow}>
                <span style={styles.activeTag}>Active</span>
                <span style={styles.legacyTag}>Legacy</span>
              </div>
              <div style={styles.cardTitleRow}>
            <div style={styles.cardTitle}>Product Database Records</div>
            <PurchaseBadge kind='monthly' />
          </div>
              <div style={styles.cardPrice}>
                Add monthly product database records
              </div>
              <select
                className='sa-control-input'
                value={selectedProductDatabaseCreditPack}
                disabled
              >
                {availableProductDatabaseCreditPacks.map((pack) => (
                  <option
                    key={`product-db-${pack.credits}`}
                    value={String(pack.credits)}
                  >
                    {pack.credits} credits ({pack.credits} rows of data)
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      <div style={styles.cardsRow}>
        <div
          style={{
            ...styles.card,
            flex: '1 1 0',
            minWidth: '260px',
            ...(activeByProduct.businessVerification ? styles.activeCard : {}),
          }}
        >
          {activeByProduct.businessVerification && (
            <span style={styles.activeTag}>Active</span>
          )}
          <div style={styles.cardTitleRow}>
            <div style={styles.cardTitle}>Business Verification Add-on</div>
            <PurchaseBadge kind='monthly' />
          </div>
          <div style={styles.cardPrice}>
            Monthly verification credits at $
            {BUSINESS_VERIFICATION_PRICE_PER_CREDIT.toFixed(2)} per credit (1
            credit = 1 entity verification)
          </div>
          {verificationSummary ? (
            <div style={styles.detailRow}>
              Credit balance: {verificationSummary.total.toLocaleString()} (
              {verificationSummary.used.toLocaleString()} used this month,{' '}
              {Math.max(
                0,
                verificationSummary.total - verificationSummary.used,
              ).toLocaleString()}{' '}
              remaining)
            </div>
          ) : null}
          {activeByProduct.businessVerification ? (
            <div style={styles.detailRow}>
              Subscribed monthly credits:{' '}
              {(
                activeByProduct.businessVerification?.items?.data?.[0]
                  ?.quantity || 0
              ).toLocaleString()}
            </div>
          ) : (
            <select
              className='sa-control-input'
              value={selectedBusinessVerificationCredits}
              onChange={(e) =>
                setSelectedBusinessVerificationCredits(e.target.value)
              }
              disabled={!businessVerificationPrice}
            >
              <option value=''>None</option>
              {BUSINESS_VERIFICATION_CREDIT_TIERS.map((credits) => (
                <option key={`bv-${credits}`} value={String(credits)}>
                  {credits.toLocaleString()} credits ($
                  {(
                    credits * BUSINESS_VERIFICATION_PRICE_PER_CREDIT
                  ).toLocaleString()}
                  /mo)
                </option>
              ))}
            </select>
          )}
        </div>
        <div
          style={{
            ...styles.card,
            flex: '1 1 0',
            minWidth: '260px',
            ...styles.disabledCard,
          }}
        >
          <div style={styles.cardTitleRow}>
            <div style={styles.cardTitle}>Corporate Ownership Check Add-on</div>
            <PurchaseBadge kind='monthly' />
          </div>
          <div style={styles.cardPrice}>
            Coming Soon -- monthly corporate ownership check credits at $
            {CORPORATE_OWNERSHIP_PRICE_PER_CREDIT.toFixed(2)} per credit (1
            credit = 1 ownership check)
          </div>
          <select className='sa-control-input' value='' disabled>
            <option value=''>Coming Soon</option>
          </select>
        </div>
      </div>

      <div style={styles.cardsRow}>
        <div
          style={{
            ...styles.card,
            flexBasis: '100%',
            ...(activeByProduct.analyzerRefresh ? styles.activeCard : {}),
            ...(isBaseAndCreditSelectionDisabled ? styles.disabledCard : {}),
          }}
        >
          {activeByProduct.analyzerRefresh && (
            <span style={styles.activeTag}>Active</span>
          )}
          <div style={styles.cardTitleRow}>
            <div style={styles.cardTitle}>Credits Auto-Refresh</div>
            <PurchaseBadge kind='monthly' />
          </div>
          <div style={styles.cardPrice}>
            Refreshes Analyzer & Workflow credits every billing cycle
          </div>
          {activeByProduct.analyzerRefresh ? (
            <div style={styles.detailRow}>
              Monthly credits:{' '}
              {activeByProduct.analyzerRefresh?.metadata?.cycled_credits ||
                activeByProduct.analyzerRefresh?.items?.data?.[0]?.metadata
                  ?.cycled_credits ||
                0}
            </div>
          ) : (
            <div style={styles.enterpriseCreditsControls}>
              <input
                type='number'
                min='1'
                step='1'
                className='sa-control-input'
                value={analyzerRefreshCredits}
                onChange={(e) => setAnalyzerRefreshCredits(e.target.value)}
                placeholder='Enter monthly credits'
                disabled={
                  isBaseAndCreditSelectionDisabled || isCreatingAnalyzerRefresh
                }
              />
              <button
                type='button'
                style={styles.selectBtn}
                onClick={handleCreateAnalyzerRefresh}
                disabled={
                  isAnalyzerRefreshInvalid ||
                  isBaseAndCreditSelectionDisabled ||
                  isCreatingAnalyzerRefresh ||
                  !analyzerRefreshPrice
                }
              >
                {isCreatingAnalyzerRefresh
                  ? 'Creating...'
                  : 'Create Subscription'}
              </button>
            </div>
          )}
        </div>
      </div>

      <div style={styles.sectionHeader}>Manual Credit Controls</div>
      <div style={styles.sectionDescription}>
        These credits are applied outside of the subscription tiers and are
        customized. The Analyzer & Workflow credits burn down, but the Product
        and Entity credits stay at the value applied below. Customers are billed
        through a manual invoice in Stripe or Quickbooks for these credits.
      </div>

      <div style={styles.groupHeader}>
        <BoltOutlinedIcon style={{ fontSize: '16px' }} />
        Direct Credits
      </div>
      <div style={styles.controlsGrid}>
        <div style={styles.card}>
          <div style={styles.cardTitleRow}>
            <div style={styles.cardTitle}>Database Records</div>
            <PurchaseBadge kind='monthly' />
          </div>
          <div style={styles.detailRow}>
            The value entered sets the team&apos;s total monthly database records
            (combined row capacity, `db_size`).
          </div>
          {hasLegacyDatabaseCreditPack && (
            <div style={styles.warningBox} role='alert'>
              <strong>Legacy database records packs detected.</strong> Adding
              combined Database Records will overwrite the legacy Entity /
              Product records packs: they are cancelled, with unused time
              credited toward the combined pack. Customers should be alerted of
              this change.
            </div>
          )}
          <div style={styles.stackedControl}>
            <input
              type='number'
              min='1'
              step='1'
              className='sa-control-input'
              value={databaseRowsToAdd}
              onChange={(e) => setDatabaseRowsToAdd(e.target.value)}
              placeholder='Enter row count'
            />
            <button
              type='button'
              style={{ ...styles.selectBtn, width: '100%' }}
              onClick={handleOpenSetDatabaseRowsModal}
              disabled={isDatabaseRowsAmountInvalid}
            >
              Set the Monthly Database Records
            </button>
          </div>
        </div>
        <div style={styles.card}>
          <div style={styles.cardTitleRow}>
            <div style={styles.cardTitle}>Credits</div>
            <PurchaseBadge kind='one-time' />
          </div>
          <div style={styles.detailRow}>
            Add extra Bulk Tariff Analyzer & Workflow credits directly to this
            team.
          </div>
          <div style={styles.stackedControl}>
            <input
              type='number'
              min='1'
              step='1'
              className='sa-control-input'
              value={analyzersCreditsToAdd}
              onChange={(e) => setAnalyzersCreditsToAdd(e.target.value)}
              placeholder='Enter credits'
            />
            <button
              type='button'
              style={{ ...styles.selectBtn, width: '100%' }}
              onClick={handleOpenAddAnalyzersCreditsModal}
              disabled={isAnalyzersCreditsAmountInvalid}
            >
              Add Credits
            </button>
          </div>
        </div>
        <div style={styles.card}>
          <div style={styles.cardTitle}>Workflow Credits</div>
          <div style={styles.detailRow}>
            When checked, this team is never charged credits for the workflow
            tools (Import, Export, Design Assistant, Beneficial Ownership).
            Analyzer, Tools and Database records are still spent as usual.
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type='checkbox'
              checked={unlimitedWorkflows}
              disabled={isSavingUnlimitedWorkflows}
              onChange={(e) => handleToggleUnlimitedWorkflows(e.target.checked)}
            />
            Unlimited workflows
          </label>
        </div>
      </div>

      <div style={styles.controlsGrid}>
        <div style={styles.card}>
          <div style={styles.cardTitleRow}>
            <div style={styles.cardTitle}>Business Verification Credits</div>
            <PurchaseBadge kind='monthly' />
          </div>
          <div style={styles.detailRow}>
            The value entered sets the team&apos;s total monthly Business
            Verification credits (1 credit = 1 entity verification).
          </div>
          <div style={styles.detailRow}>
            Entering a value here overrides the subscription value in Stripe.
            {stripeVerificationQuantity > 0 && (
              <>
                {' '}
                The Stripe value will remain at its current value of{' '}
                <strong>{stripeVerificationQuantity.toLocaleString()}</strong>,
                but the value entered will be the limit Entity Hub will use to
                gate the credit usage.
              </>
            )}
          </div>
          <div style={styles.stackedControl}>
            <input
              type='number'
              min='0'
              step='1'
              className='sa-control-input'
              value={verificationCreditsToSet}
              onChange={(e) => setVerificationCreditsToSet(e.target.value)}
              placeholder='Enter credit count'
            />
            <button
              type='button'
              style={{ ...styles.selectBtn, width: '100%' }}
              onClick={() => setIsSetVerificationModalOpen(true)}
              disabled={isVerificationCreditsInvalid}
            >
              Set the Monthly Business Verification Credits
            </button>
          </div>
          <div style={styles.detailRow}>
            Add Credits Back lowers the credits spent this month by the number
            entered (for example, to refund verifications that should not have
            been charged). It does not change the total monthly credit
            allowance, and it cannot exceed the credits spent so far this
            month.
          </div>
          <div style={styles.stackedControl}>
            <input
              type='number'
              min='1'
              step='1'
              className='sa-control-input'
              value={verificationAddBack}
              onChange={(e) => setVerificationAddBack(e.target.value)}
              placeholder='Enter credits to add back'
            />
            {verificationAddBack.trim() !== '' &&
              isVerificationAddBackInvalid && (
                <div style={styles.detailRow} role='status'>
                  {!verificationSummary
                    ? 'Loading this month’s credit usage…'
                    : verificationSpent === 0
                    ? 'No credits have been spent this month, so there is nothing to add back.'
                    : `Enter a whole number from 1 to ${verificationSpent.toLocaleString()} (credits spent this month).`}
                </div>
              )}
            <button
              type='button'
              style={{ ...styles.selectBtn, width: '100%' }}
              onClick={() => setIsAddBackModalOpen(true)}
              disabled={isVerificationAddBackInvalid}
            >
              Add Credits Back to Account
            </button>
          </div>
        </div>
        <div style={{ ...styles.card, ...styles.disabledCard }}>
          <div style={styles.cardTitleRow}>
            <div style={styles.cardTitle}>Corporate Ownership Credits</div>
            <PurchaseBadge kind='monthly' />
          </div>
          <div style={styles.detailRow}>
            Coming Soon -- set the Corporate Ownership Check credit balance (1
            credit = 1 ownership check) for this team.
          </div>
          <div style={styles.stackedControl}>
            <input
              type='number'
              className='sa-control-input'
              placeholder='Coming Soon'
              disabled
            />
            <button
              type='button'
              style={{ ...styles.selectBtn, width: '100%' }}
              disabled
            >
              Set Ownership Credits
            </button>
          </div>
        </div>
      </div>

      {/* <div style={styles.sectionHeader}>Enterprise</div>
      <div style={styles.cardsRow}>
        {enterpriseConfigs.map((plan) => {
          const isActive = Boolean(activeByProduct[plan.id]);
          const isSelected = selectedEnterprisePlanId === plan.id;
          const disablePlan =
            isEnterpriseSelectionActive && !isSelected && !isActive;
          const disableByExisting =
            hasActiveEnterprisePlan && !isActive && !isSelected;
          return (
            <div
              key={plan.id}
              style={{
                ...styles.card,
                ...(isActive || isSelected ? styles.activeCard : {}),
                ...(disablePlan || disableByExisting
                  ? styles.disabledCard
                  : {}),
              }}
            >
              {(isActive || isSelected) && (
                <span style={styles.activeTag}>Active</span>
              )}
              <div style={styles.cardTitle}>{plan.label}</div>
              <div style={styles.detailRow}>Licenses: {plan.fixedQuantity}</div>
              <div style={styles.detailRow}>
                Monthly credits: {plan.fixedCredits.toLocaleString()}
              </div>
              {!isActive && (
                <button
                  type='button'
                  style={styles.selectBtn}
                  onClick={() =>
                    setSelectedEnterprisePlanId((prev) =>
                      prev === plan.id ? null : plan.id,
                    )
                  }
                  disabled={disablePlan || disableByExisting}
                >
                  {isSelected ? 'Deselect' : 'Select'}
                </button>
              )}
            </div>
          );
        })}
      </div> */}

      <div style={styles.sectionHeader}>Entity Screen Add-on</div>
      <div style={styles.cardsRow}>
        <div style={{ ...styles.card, maxWidth: '420px' }}>
          <div style={styles.cardTitle}>Entity Screen Add-on</div>
          <div style={styles.detailRow}>
            Toggle temporary access on or off for this team.
          </div>
          <div style={styles.enterpriseCreditsControls}>
            <button
              type='button'
              style={{
                ...styles.selectBtn,
                backgroundColor: isEntityScreenAddonEnabled
                  ? 'var(--action-color)'
                  : 'var(--bg-surface)',
                color: isEntityScreenAddonEnabled
                  ? '#fff'
                  : 'var(--text-primary-color)',
                border: isEntityScreenAddonEnabled
                  ? '1px solid var(--action-color)'
                  : '1px solid var(--es-soft-border)',
              }}
              onClick={() => {
                (async () => {
                  if (!curTeam?.id || !entityAddonId) {
                    enqueueSnackbar(
                      'Unable to enable Entity Screen Add-on: missing team or role configuration.',
                      { variant: 'error' },
                    );
                    return;
                  }

                  const response = await GroupAPI.addRoleToGroup(
                    curTeam.id,
                    entityAddonId,
                    'entity-addon',
                  );

                  if (!response?.success) {
                    enqueueSnackbar(
                      response?.result ||
                        'Failed to enable Entity Screen Add-on.',
                      {
                        variant: 'error',
                      },
                    );
                    return;
                  }

                  setIsEntityScreenAddonEnabled(true);
                  enqueueSnackbar('Entity Screen Add-on enabled.', {
                    variant: 'success',
                  });
                })();
              }}
            >
              Enabled
            </button>
            <button
              type='button'
              style={{
                ...styles.selectBtn,
                backgroundColor: !isEntityScreenAddonEnabled
                  ? 'var(--action-color)'
                  : 'var(--bg-surface)',
                color: !isEntityScreenAddonEnabled
                  ? '#fff'
                  : 'var(--text-primary-color)',
                border: !isEntityScreenAddonEnabled
                  ? '1px solid var(--action-color)'
                  : '1px solid var(--es-soft-border)',
              }}
              onClick={() => {
                (async () => {
                  if (!curTeam?.id || !entityAddonId) {
                    enqueueSnackbar(
                      'Unable to disable Entity Screen Add-on: missing team or role configuration.',
                      { variant: 'error' },
                    );
                    return;
                  }

                  const response = await GroupAPI.removeRoleFromGroup(
                    curTeam.id,
                    entityAddonId,
                    'entity-addon',
                  );

                  if (!response?.success) {
                    enqueueSnackbar(
                      response?.result ||
                        'Failed to disable Entity Screen Add-on.',
                      {
                        variant: 'error',
                      },
                    );
                    return;
                  }

                  setIsEntityScreenAddonEnabled(false);
                  enqueueSnackbar('Entity Screen Add-on disabled.', {
                    variant: 'info',
                  });
                })();
              }}
            >
              Disabled
            </button>
          </div>
        </div>
      </div>

      <div style={styles.actionsRow}>
        <Button
          variant='contained'
          color='primary'
          style={styles.viewStripeButton}
          onClick={handleViewInStripe}
        >
          View Customer in Stripe
        </Button>
        <Button
          variant='contained'
          className='Theme-Btn'
          type='button'
          disabled={!hasPendingChanges || isCreatingSubscriptions}
          onClick={async () => {
            const basePlansToSubmit = productConfigs.filter((product) => {
              const productId = product.id as keyof typeof initialQuantities;
              return (
                (quantities[productId] || 0) > initialQuantities[productId]
              );
            });

            // Only the base plans in the payload can carry a trial, so a stale
            // duration on an untouched plan must not block credit-pack-only
            // purchases.
            const hasInvalidTrialDuration = basePlansToSubmit.some(
              (product) => {
                if (!basePlanTrialToggles[product.id]) {
                  return false;
                }

                const days = Number(basePlanTrialDays[product.id]);
                return (
                  !Number.isInteger(days) ||
                  days <= 0 ||
                  days > MAX_FREE_TRIAL_DAYS
                );
              },
            );

            if (hasInvalidTrialDuration) {
              enqueueSnackbar(
                `Free trial duration must be a whole number between 1 and ${MAX_FREE_TRIAL_DAYS} days.`,
                { variant: 'warning' },
              );
              return;
            }

            setIsCreatingSubscriptions(true);
            try {
              await adminCreateSubscription(
                customerInfo?.data?.id,
                selectedEnterprisePlan
                  ? [
                      {
                        product: selectedEnterprisePlan,
                        quantity: selectedEnterprisePlan.fixedQuantity,
                      },
                    ]
                  : basePlansToSubmit.map((product) => {
                      const productId =
                        product.id as keyof typeof initialQuantities;
                      return {
                        product: product,
                        quantity: quantities[productId],
                        freeTrialEnabled: basePlanTrialToggles[product.id],
                        freeTrialDays: Number(basePlanTrialDays[product.id]),
                      };
                    }),
                selectedEnterprisePlanId
                  ? undefined
                  : [
                      ...(businessVerificationSelectionChanged
                        ? [
                            {
                              credits: selectedBusinessVerificationCredits,
                              isDatabase: false,
                              isBusinessVerification: true,
                            },
                          ]
                        : []),
                      ...(combinedDatabaseCreditPackSelectionChanged
                        ? [
                            {
                              credits: selectedCombinedDatabaseCreditPack,
                              isDatabase: true,
                              isCombinedDatabase: true,
                            },
                          ]
                        : []),
                      ...(databaseCreditPackSelectionChanged
                        ? [
                            {
                              credits: selectedDatabaseCreditPack,
                              isDatabase: true,
                            },
                          ]
                        : []),
                      ...(productDatabaseCreditPackSelectionChanged
                        ? [
                            {
                              credits: selectedProductDatabaseCreditPack,
                              isDatabase: false,
                              isProductDatabase: true,
                            },
                          ]
                        : []),
                    ],
              );
            } finally {
              setIsCreatingSubscriptions(false);
            }
          }}
        >
          <span style={styles.createBtnContent}>
            {isCreatingSubscriptions && (
              <CircularProgress size={16} color='inherit' />
            )}
            {isCreatingSubscriptions
              ? 'Creating Subscriptions...'
              : 'Create Selected Subscriptions'}
          </span>
        </Button>
      </div>

      <Modal
        open={isSetDatabaseRowsModalOpen}
        onClose={() => {
          if (!isSettingDatabaseRows) {
            setIsSetDatabaseRowsModalOpen(false);
          }
        }}
      >
        <ConfirmActionModal
          title='Confirm Database Row Update'
          description={
            <>
              Set <strong>{curTeam?.name}</strong> database records to{' '}
              <strong>{parsedDatabaseRowsToAdd.toLocaleString()}</strong> rows?
              This replaces the team&apos;s current database record total. It
              does not add to it.
              {databaseSummary && (
                <>
                  <br />
                  <br />
                  Account adjustment:
                  <br />
                  Current total records:{' '}
                  <strong>{databaseSummary.total.toLocaleString()}</strong>
                  <br />
                  Adjusted total records:{' '}
                  <strong>{parsedDatabaseRowsToAdd.toLocaleString()}</strong>
                  <br />
                  Records in use:{' '}
                  <strong>{databaseSummary.used.toLocaleString()}</strong>
                  <br />
                  New remaining records:{' '}
                  <strong>
                    {Math.max(
                      0,
                      parsedDatabaseRowsToAdd - databaseSummary.used,
                    ).toLocaleString()}
                  </strong>
                </>
              )}
            </>
          }
          isProcessing={isSettingDatabaseRows}
          processingLabel='Updating'
          confirmLabel='Confirm'
          onCancel={() => setIsSetDatabaseRowsModalOpen(false)}
          onConfirm={handleConfirmSetDatabaseRows}
        />
      </Modal>

      <Modal
        open={isSetVerificationModalOpen}
        onClose={() => {
          if (!isSettingVerification) {
            setIsSetVerificationModalOpen(false);
          }
        }}
      >
        <ConfirmActionModal
          title='Confirm Business Verification Credit Update'
          description={
            <>
              This will update the Monthly Business Credits for{' '}
              <strong>{curTeam?.name}</strong> to{' '}
              <strong>{parsedVerificationCreditsToSet.toLocaleString()}</strong>
              . The team will be allowed to run Business Verification{' '}
              <strong>{parsedVerificationCreditsToSet.toLocaleString()}</strong>{' '}
              {parsedVerificationCreditsToSet === 1 ? 'time' : 'times'} per
              month.
              <br />
              <br />
              This overrides the subscription value in Stripe.
              {stripeVerificationQuantity > 0 && (
                <>
                  {' '}
                  The Stripe value will remain at its current value of{' '}
                  <strong>{stripeVerificationQuantity.toLocaleString()}</strong>,{' '}
                  but the value entered will be the limit Entity Hub will use
                  to gate the credit usage. The new value will be{' '}
                  <strong>
                    {parsedVerificationCreditsToSet.toLocaleString()}
                  </strong>
                  .
                </>
              )}
            </>
          }
          isProcessing={isSettingVerification}
          processingLabel='Updating'
          confirmLabel='Confirm'
          onCancel={() => setIsSetVerificationModalOpen(false)}
          onConfirm={handleConfirmSetVerificationCredits}
        />
      </Modal>

      <Modal
        open={isAddBackModalOpen}
        onClose={() => {
          if (!isAddingBack) {
            setIsAddBackModalOpen(false);
          }
        }}
      >
        <ConfirmActionModal
          title='Confirm Add Credits Back'
          description={
            <>
              Add{' '}
              <strong>{parsedVerificationAddBack.toLocaleString()}</strong>{' '}
              Business Verification credits back to{' '}
              <strong>{curTeam?.name}</strong>?
              <br />
              <br />
              Credits spent this month will go from{' '}
              <strong>{verificationSpent.toLocaleString()}</strong> to{' '}
              <strong>
                {Math.max(
                  0,
                  verificationSpent - (parsedVerificationAddBack || 0),
                ).toLocaleString()}
              </strong>
              , and remaining credits will increase by{' '}
              <strong>{parsedVerificationAddBack.toLocaleString()}</strong>.
              The total monthly credit allowance
              {verificationSummary
                ? ` (${verificationSummary.total.toLocaleString()})`
                : ''}{' '}
              does not change. The adjustment applies to the current credit
              period only, is recorded with your name, and cannot be undone
              from this screen.
            </>
          }
          isProcessing={isAddingBack}
          processingLabel='Adding'
          confirmLabel='Confirm'
          onCancel={() => setIsAddBackModalOpen(false)}
          onConfirm={handleConfirmAddBackVerificationCredits}
        />
      </Modal>

      <Modal
        open={isAddWorkflowCreditsModalOpen}
        onClose={() => {
          if (!isAddingWorkflowCredits) {
            setIsAddWorkflowCreditsModalOpen(false);
          }
        }}
      >
        <ConfirmActionModal
          title='Confirm Workflow Credit Addition'
          description={
            <>
              Add <strong>{parsedWorkflowCreditsToAdd.toLocaleString()}</strong>{' '}
              workflow credits to <strong>{curTeam?.name}</strong>?
            </>
          }
          isProcessing={isAddingWorkflowCredits}
          processingLabel='Adding'
          confirmLabel='Confirm'
          onCancel={() => setIsAddWorkflowCreditsModalOpen(false)}
          onConfirm={handleConfirmAddWorkflowCredits}
        />
      </Modal>

      <Modal
        open={isAddAnalyzersCreditsModalOpen}
        onClose={() => {
          if (!isAddingAnalyzersCredits) {
            setIsAddAnalyzersCreditsModalOpen(false);
          }
        }}
      >
        <ConfirmActionModal
          title='Confirm Credits Addition'
          description={
            <>
              Add{' '}
              <strong>{parsedAnalyzersCreditsToAdd.toLocaleString()}</strong>{' '}
              analyzer & workflow credits to <strong>{curTeam?.name}</strong>.
              These credits will be added on top of the team&apos;s current total,
              not replace it.
              {analyzersSummary && (
                <>
                  <br />
                  <br />
                  Account adjustment:
                  <br />
                  Current total credits:{' '}
                  <strong>{analyzersSummary.total.toLocaleString()}</strong>
                  <br />
                  Adjusted total credits:{' '}
                  <strong>
                    {(
                      analyzersSummary.total + parsedAnalyzersCreditsToAdd
                    ).toLocaleString()}
                  </strong>
                  <br />
                  New remaining credits:{' '}
                  <strong>
                    {(
                      analyzersSummary.remaining + parsedAnalyzersCreditsToAdd
                    ).toLocaleString()}
                  </strong>
                </>
              )}
            </>
          }
          isProcessing={isAddingAnalyzersCredits}
          processingLabel='Adding'
          confirmLabel='Confirm'
          onCancel={() => setIsAddAnalyzersCreditsModalOpen(false)}
          onConfirm={handleConfirmAddAnalyzersCredits}
        />
      </Modal>
    </div>
  );
};

export default EditAccess;
